import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ccConfig, CC, Search, SearchType } from '../CC/CC';
import { CCProvider, useCCContext } from '../CC/CCContext';
import GenericCheckbox from '../GenericComponents/GenericCheckbox';
import GenericIconButton from '../GenericComponents/GenericIconButton';
import GenericModal from '../GenericComponents/GenericModal';
import * as ccUtils from '../CC/utils';
import styles from './MobileSearch.module.css';
import MobileQuerySummary from './MobileQuerySummary';
import Count from '../Navigator/Count';
import MobileBrowseValues from './MobileBrowseValues';
import MobileScrollResults from './MobileScrollResults';
import MobileMap from './MobileMap';
import Map from '../Record/Map';
import Icons from '../Result/Icons';

interface SearchValue {
    source: string;
    text: string;
}

type SearchProps = {
    mainY: number;
    setAndKeepMainY: (newMainY: number, prevMainY: number) => void;
    selectedRecord: any | null;
    setSelectedRecord: React.Dispatch<React.SetStateAction<any | null>>;
};

const MobileSearch: React.FC<SearchProps> = ({ mainY, setAndKeepMainY, selectedRecord, setSelectedRecord }) => {
    const { t } = useTranslation();
    const { cc, setCc, viewMode, setViewMode } = useCCContext();
    const ctx = useCCContext();
    const [selectedField, setSelectedField] = useState<string>(Object.keys(ccConfig.mobileSearchFields)[0]);
    const [searchText, setSearchText] = useState<string>("");
    const [showMap, setShowMap] = useState<boolean>(false);
    const [showBrowseValues, setShowBrowseValues] = useState(false);
    const topRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    setViewMode("icons");

    const showHomepage = () => {
        setAndKeepMainY(-100, -200);
    }

    const handleValueSelection = (selectedValue: string) => {
        if (topRef.current) {
            topRef.current.scrollIntoView();
        }
        setSearchText("");
        setShowBrowseValues(!showBrowseValues);

        let encodedValue = selectedValue.replace(/\[/g, encodeURIComponent('[')).replace(/\]/g, encodeURIComponent(']'));
        setTimeout(() => {
            ccUtils.addCustomSearch(ctx, ccConfig.mobileSearchFields[selectedField].field, selectedField, "[" + encodedValue + "]");
        }, 10);
    }

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            executeSearch();
        }
    };

    const executeSearch = () => {
        if (searchText.trim() != "") {
            ccUtils.addCustomSearch(ctx, ccConfig.mobileSearchFields[selectedField].field, selectedField, searchText);
        }
        setSearchText("");
        setShowBrowseValues(false);
    };

    const handleMatchAllChange = (checked: boolean, value: string) => {
        //setMatchAll(checked);
        setCc({
            ...cc,
            matchAllCriteria: checked,
        });
    };

    return (
        <div className={styles.MobileSearch}>
            <div ref={topRef} className={styles.header}>
                <div className={styles.topLeftButton}>
                    <GenericIconButton
                        icon='light/house.svg'
                        hoverEnabled={true}
                        width='28px'
                        height='28px'
                        onClick={(e: any) => {
                            showHomepage()
                        }}
                    />
                </div>
                <a href="https://www.quaibranly.fr/" className={styles.logo}></a>
            </div>
            <div className={styles.searchbox}>
                <div style={{ 'display': 'none' }}>
                    <GenericCheckbox
                        id={'mobileSearchConformToAllCriteria'}
                        name={'mobileSearchConformToAllCriteria'}
                        value={''}
                        checked={cc.matchAllCriteria}
                        onChange={handleMatchAllChange}
                        label={t("mobile_all_criteria")}
                    />
                </div>
                <select
                    value={selectedField}
                    onChange={e => {
                        setSelectedField(e.target.value);
                        // Check if the selected field has browse set to true
                        if (ccConfig.mobileSearchFields[e.target.value].browse) {
                            setShowBrowseValues(true);
                        } else {
                            setShowBrowseValues(false);
                        }
                        if (inputRef.current) {
                            inputRef.current.focus();
                        }
                    }}
                >
                    {Object.keys(ccConfig.mobileSearchFields).map(key => (
                        <option key={key} value={key}>{t(key)}</option>
                    ))}
                </select>

                <div className={styles.searchInputContainer}>
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchText}
                        onChange={e => {
                            setSearchText(e.target.value);
                            if (e.target.value) {
                                setShowBrowseValues(true);
                            }
                        }}
                        onKeyPress={handleKeyPress}
                    />
                    {ccConfig.mobileSearchFields[selectedField].browse ? (
                        <GenericIconButton isActive={showBrowseValues} width='32px' height='32px' icon='light/filter.svg' onClick={() => setShowBrowseValues(!showBrowseValues)} />
                    ) : ("")
                    }
                    <GenericIconButton isActive={showBrowseValues} width='32px' height='32px' icon='light/map-marked-alt.svg' onClick={() => setShowMap(true)} />
                </div>

                <button onClick={executeSearch}>Search</button>

            </div>

            <div className={`${styles.browseValues} ${showBrowseValues ? styles.show : ''}`}>
                {ccConfig.mobileSearchFields[selectedField].browse ? (
                    <MobileBrowseValues field={ccConfig.mobileSearchFields[selectedField].field} searchText={searchText} onValueSelected={handleValueSelection}></MobileBrowseValues>
                ) : ("")
                }
            </div>

            <div className={`${styles.mobileResults} ${showBrowseValues ? '' : styles.show}`}>
                <MobileQuerySummary></MobileQuerySummary>
                <MobileScrollResults mainY={mainY} setAndKeepMainY={setAndKeepMainY} selectedRecord={selectedRecord} setSelectedRecord={setSelectedRecord} />
            </div>

            {showMap &&
                <div className={styles.modalMap}>
                    <div className={styles.map}>
                        <div className={styles.closeButton}>
                            <GenericIconButton icon='light/times.svg' width='28px' height='28px' onClick={() => { setShowMap(false) }} />
                        </div>
                        <MobileMap dzi="https://collections.quaibranly.fr/cc/imageproxy.ashx?server=localhost&port=15012&filename=images/MQB_CC3V2_02_21031210_PH.dzi" />
                    </div>
                </div>
            }

        </div>
    );
}

export default MobileSearch;
